import { Chip } from '@/components/ui/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/classUtils';
import { type COLORS } from '@ditto/shared';
import { ChevronDown, X } from 'lucide-react';
import * as React from 'react';
import { Ellipsis } from './ellipsis';

const COLOR_MAP: Record<(typeof COLORS)[number], string> = {
  yellow: '#ffed7e',
  blue: '#B0D4FF',
  magenta: '#F3C0F5',
  green: '#A1FAA4',
  purple: '#B2B6FF',
  orange: '#FFCB87',
  red: '#fecaca',
  tan: '#F9FAF5',
};

export interface MultiselectRef {
  close: () => void;
}

export interface MultiselectOption {
  label: string;
  value: string;
  color?: (typeof COLORS)[number];
}

interface MultiselectProps {
  options: MultiselectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  showCount?: boolean;
  showClearAll?: boolean;
  showSelectAll?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  trigger?: React.ReactNode;
  triggerLabel?: string;
  onClear?: () => void;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}

export const Multiselect = React.forwardRef<MultiselectRef, MultiselectProps>(
  (
    {
      options,
      selected,
      onChange,
      placeholder = 'Select items...',
      className,
      contentClassName,
      showClearAll = true,
      showSelectAll = false,
      showSearch = true,
      searchPlaceholder,
      trigger,
      triggerLabel,
      onClear,
      onOpenChange,
      align = 'start',
      alignOffset,
      side,
      sideOffset,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [enableInteraction, setEnableInteraction] = React.useState(true);

    React.useImperativeHandle(ref, () => ({
      close: () => setOpen(false),
    }));

    // Disable interaction briefly when opening to prevent accidental clicks
    React.useEffect(() => {
      if (open) {
        setEnableInteraction(false);
        const timer = setTimeout(() => setEnableInteraction(true), 150);
        return () => clearTimeout(timer);
      }
    }, [open]);

    const filterOptions: FilterOption[] = React.useMemo(
      () =>
        options.map(opt => ({
          label: opt.label,
          value: opt.value,
          color: opt.color ? COLOR_MAP[opt.color] : undefined,
        })),
      [options]
    );

    const handleFilterChange = React.useCallback(
      (newSelected: string[]) => {
        if (!enableInteraction) return;
        onChange(newSelected);
      },
      [enableInteraction, onChange]
    );

    const clearAll = () => {
      onChange([]);
      onClear?.();
      setOpen(false);
    };

    const selectAll = () => {
      onChange(options.map(option => option.value));
    };

    const isAllSelected =
      selected.length === options.length && options.length > 0;

    const handleClearClick = (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onChange([]);
      onClear?.();
      setOpen(false);
    };

    const handleClearMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleClearClickEvent = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const getDefaultTrigger = () => {
      const label = triggerLabel || placeholder;
      const hasSelection = selected.length > 0;

      if (hasSelection) {
        const selectedOption =
          selected.length === 1
            ? options.find(option => option.value === selected[0])
            : null;
        const displayText =
          selected.length === 1
            ? selectedOption?.label || selected[0]
            : `${selected.length} selected`;

        return (
          <Chip
            className={cn(
              '!bg-yellow-50 hover:!bg-yellow-100 focus-visible:!bg-yellow-50 focus-visible:!border-neutral-300 focus-visible:outline-none active:!bg-yellow-100',
              className
            )}
          >
            <Chip.Label>
              <FlexRow gap="1.5" alignItems="center" className="min-w-0">
                <Ellipsis asChild lineClamp={1} hasTooltip>
                  <Text
                    variant="body-sm"
                    className="font-medium text-neutral-900 min-w-0"
                  >
                    <span data-label>{label}</span>:{' '}
                    <span className="font-bold">{displayText}</span>
                  </Text>
                </Ellipsis>

                <button
                  type="button"
                  onPointerDown={handleClearClick}
                  onMouseDown={handleClearMouseDown}
                  onClick={handleClearClickEvent}
                  className="shrink-0 flex items-center justify-center hover:bg-yellow-100 rounded p-0.5"
                >
                  <X size={16} className="text-neutral-600" />
                </button>
              </FlexRow>
            </Chip.Label>
          </Chip>
        );
      }

      return (
        <Chip className={className}>
          <Chip.Label>
            <FlexRow
              gap="1.5"
              alignItems="center"
              className="whitespace-nowrap"
            >
              <Text variant="body-sm" className="font-medium text-neutral-900">
                <span data-label className="truncate-label">
                  {label}
                </span>
              </Text>
              <ChevronDown size={16} className="text-neutral-600" />
            </FlexRow>
          </Chip.Label>
        </Chip>
      );
    };

    return (
      <DropdownMenu
        open={open}
        onOpenChange={newOpen => {
          setOpen(newOpen);
          onOpenChange?.(newOpen);
        }}
      >
        <DropdownMenuTrigger asChild>
          {trigger || getDefaultTrigger()}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn('w-56', contentClassName)}
          align={align}
          alignOffset={alignOffset}
          side={side}
          sideOffset={sideOffset}
          onCloseAutoFocus={e => e.preventDefault()}
        >
          {showSelectAll && options.length > 0 && !isAllSelected && (
            <>
              <div className="px-3 py-2 flex justify-end">
                <button
                  onClick={selectAll}
                  className="text-sm text-neutral-900 hover:text-black cursor-pointer"
                >
                  Select All
                </button>
              </div>
              <DropdownMenuSeparator />
            </>
          )}
          <FilterOptionList
            options={filterOptions}
            selected={selected}
            onChange={handleFilterChange}
            showSearch={showSearch}
            searchPlaceholder={searchPlaceholder}
          />
          {showClearAll && selected.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="px-3 py-2 flex justify-end">
                <button
                  onClick={clearAll}
                  className="text-sm text-neutral-900 hover:text-black cursor-pointer"
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

Multiselect.displayName = 'Multiselect';
